import { Sparkles, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { LookalikeView, Persona } from '@repo/contracts';
import { CANVAS_CREDITS } from '@repo/contracts/credits';

import { PersonaAvatar } from '../persona/persona-avatar';
import {
  type LookalikeAxisField,
  type LookalikeFilterField,
  usePersonaLookalikesStore,
} from '../store/persona-lookalikes-store';
import { CATEGORICAL_FIELDS, categoricalValueOf } from './lookalike-fields';
import { AXIS_LABELS } from './lookalike-layout';
import { THIN_SCROLLBAR } from './lookalike-scrollbar';
import { LookalikesChart } from './lookalikes-chart';
import { LookalikesClusters } from './lookalikes-clusters';
import { LookalikesPoll } from './lookalikes-poll';
import { LookalikesTable } from './lookalikes-table';
import { LookalikesWeights } from './lookalikes-weights';
import { useLookalikePoll } from './use-lookalike-poll';
import { usePersonaLookalikes } from './use-persona-lookalikes';

const AXIS_OPTIONS: LookalikeAxisField[] = [
  'age',
  'experienceYears',
  ...CATEGORICAL_FIELDS.map((f) => f.personaField),
];

const FILTER_FIELDS: LookalikeFilterField[] = CATEGORICAL_FIELDS.map(
  (f) => f.personaField,
);

const FILTER_FIELD_LABELS: Record<LookalikeFilterField, string> =
  Object.fromEntries(
    CATEGORICAL_FIELDS.map((f) => [f.personaField, f.label]),
  ) as Record<LookalikeFilterField, string>;

/**
 * What a lookalike actually drew for this field, biggest group first — not
 * what the schema *could* have produced. Reading the schema's pool instead
 * would offer every value the model wrote regardless of whether the sample
 * contains it, and picking one that got zero draws shows an empty chart for
 * no visible reason.
 */
export function filterOptions(
  view: LookalikeView | null,
  field: LookalikeFilterField | null,
): { count: number; value: string }[] {
  if (!view || !field) return [];
  const counts = new Map<string, number>();
  for (const lookalike of view.lookalikes) {
    const value = categoricalValueOf(lookalike, field);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ count, value }))
    .sort((a, b) => b.count - a.count);
}

function GenerateCta({
  error,
  generating,
  onGenerate,
}: {
  error: string | null;
  generating: boolean;
  onGenerate: (count: number) => void;
}) {
  const [count, setCount] = useState(1000);
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <span className="flex size-12 items-center justify-center rounded-button bg-highlight-soft text-ink">
        <Sparkles aria-hidden="true" className="size-5" />
      </span>
      <p className="mt-4 text-heading-xs font-semibold text-text-primary">
        Generate lookalikes
      </p>
      <p className="mt-1.5 max-w-[42ch] text-ui leading-relaxed text-text-secondary">
        One model call writes the population this persona is drawn from; every
        lookalike after that is sampled instantly, for free.
      </p>

      <div className="mt-6 w-full max-w-[280px]">
        <div className="flex items-baseline justify-between text-caption text-text-muted">
          <span>Lookalikes to sample</span>
          <span className="font-mono tabular-nums text-text-secondary">
            {count.toLocaleString()}
          </span>
        </div>
        <input
          className="mt-1.5 h-1.5 w-full cursor-pointer accent-brand"
          max={1000}
          min={100}
          onChange={(event) => {
            setCount(Number(event.target.value));
          }}
          step={50}
          type="range"
          value={count}
        />
      </div>

      <button
        className="mt-5 flex h-10 cursor-pointer items-center gap-2 rounded-button bg-brand px-4 text-ui-strong font-semibold text-white transition hover:bg-brand-alt active:scale-[0.98] disabled:cursor-default disabled:opacity-60"
        disabled={generating}
        onClick={() => {
          onGenerate(count);
        }}
        type="button"
      >
        <Sparkles aria-hidden="true" className="size-4" />
        {generating
          ? 'Writing the population…'
          : `Generate ${count.toLocaleString()} lookalikes (${CANVAS_CREDITS.PERSONA_LOOKALIKES} credits)`}
      </button>
      {error ? (
        <p className="mt-3 text-caption text-critical" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The one place server activity always shows — generating, applying weights,
 * or the initial load all light this the same way, so "something is talking
 * to the server right now" never depends on remembering which button you
 * pressed. Collapses to nothing (not just invisible) when idle, so it never
 * reads as a permanent fixture of the header.
 */
function ProgressBar({ active }: { active: boolean }) {
  return (
    <div
      aria-hidden={!active}
      className={`w-full overflow-hidden bg-hairline ${active ? 'h-0.5' : 'h-0'}`}
      role="progressbar"
    >
      {active ? (
        <div className="h-full w-1/3 animate-shimmer bg-brand motion-reduce:hidden" />
      ) : null}
    </div>
  );
}

function SegmentedButton({
  children,
  onClick,
  selected,
}: {
  children: React.ReactNode;
  onClick: () => void;
  selected: boolean;
}) {
  return (
    <button
      aria-pressed={selected}
      className={[
        'cursor-pointer rounded-button px-2.5 py-1.5 text-ui font-medium transition',
        selected
          ? 'bg-surface text-text-primary shadow-soft'
          : 'text-text-secondary hover:text-text-primary',
      ].join(' ')}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

export function LookalikesModal({
  onClose,
  persona,
}: {
  onClose: () => void;
  persona: Persona;
}) {
  const {
    generate,
    generateError,
    generating,
    loading,
    updateWeights,
    updatingWeights,
    view,
  } = usePersonaLookalikes(persona.id);
  const {
    error: pollError,
    polling,
    result: pollResult,
    run: runPoll,
  } = useLookalikePoll(persona.id);
  const displayMode = usePersonaLookalikesStore((s) => s.displayMode);
  const setDisplayMode = usePersonaLookalikesStore((s) => s.setDisplayMode);
  const viewMode = usePersonaLookalikesStore((s) => s.view);
  const setView = usePersonaLookalikesStore((s) => s.setView);
  const xField = usePersonaLookalikesStore((s) => s.xField);
  const yField = usePersonaLookalikesStore((s) => s.yField);
  const setAxis = usePersonaLookalikesStore((s) => s.setAxis);
  const filterField = usePersonaLookalikesStore((s) => s.filterField);
  const filterValue = usePersonaLookalikesStore((s) => s.filterValue);
  const setFilter = usePersonaLookalikesStore((s) => s.setFilter);

  const filterValueOptions = useMemo(
    () => filterOptions(view, filterField),
    [view, filterField],
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/25 p-6 duration-200 animate-in fade-in">
      <button
        aria-label="Close"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        type="button"
      />

      <div className="relative flex h-[min(780px,90vh)] w-full max-w-[1180px] flex-col overflow-hidden rounded-panel bg-surface shadow-floating duration-200 animate-in zoom-in-95 motion-reduce:animate-none">
        <header className="flex items-start justify-between gap-4 border-b border-hairline px-6 pt-5 pb-4">
          <div className="flex min-w-0 items-center gap-3">
            <PersonaAvatar persona={persona} size="lg" />
            <div className="min-w-0">
              <p className="text-small-label text-text-muted uppercase">
                Lookalikes
              </p>
              <h2 className="!mt-1 !mb-0 !text-2xl !leading-tight !font-semibold !text-text-primary">
                {persona.name}
              </h2>
              <p className="mt-0.5 truncate text-caption text-text-secondary">
                {persona.title}
              </p>
            </div>
          </div>
          <button
            aria-label="Close"
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-secondary transition hover:bg-surface-hover hover:text-text-primary"
            onClick={onClose}
            type="button"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        </header>

        <ProgressBar
          active={loading || generating || updatingWeights || polling}
        />

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-ui text-text-muted" role="status">
              Loading…
            </p>
          </div>
        ) : !view ? (
          <GenerateCta
            error={generateError}
            generating={generating}
            onGenerate={generate}
          />
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-[240px_minmax(0,1fr)]">
            <aside className="flex min-h-0 flex-col border-r border-hairline p-4">
              <LookalikesWeights
                applying={updatingWeights}
                key={view.spec.updatedAt}
                onApply={updateWeights}
                schema={view.spec.schema}
              />
            </aside>

            <div className="flex min-h-0 flex-col">
              <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-2.5">
                <div className="flex items-center gap-1 rounded-button bg-warm-alt p-1">
                  <SegmentedButton
                    onClick={() => {
                      setView('field');
                    }}
                    selected={viewMode === 'field'}
                  >
                    Field view
                  </SegmentedButton>
                  <SegmentedButton
                    onClick={() => {
                      setView('similarity');
                    }}
                    selected={viewMode === 'similarity'}
                  >
                    Similarity map
                  </SegmentedButton>
                </div>

                {viewMode === 'field' ? (
                  <div className="flex items-center gap-1.5 text-caption text-text-secondary">
                    <span>X</span>
                    <select
                      className="rounded-button bg-warm-alt px-2 py-1 text-caption text-text-primary"
                      onChange={(event) => {
                        setAxis('x', event.target.value as LookalikeAxisField);
                      }}
                      value={xField}
                    >
                      {AXIS_OPTIONS.map((field) => (
                        <option key={field} value={field}>
                          {AXIS_LABELS[field]}
                        </option>
                      ))}
                    </select>
                    <span>Y</span>
                    <select
                      className="rounded-button bg-warm-alt px-2 py-1 text-caption text-text-primary"
                      onChange={(event) => {
                        setAxis('y', event.target.value as LookalikeAxisField);
                      }}
                      value={yField}
                    >
                      {AXIS_OPTIONS.map((field) => (
                        <option key={field} value={field}>
                          {AXIS_LABELS[field]}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div className="flex items-center gap-1.5 text-caption text-text-secondary">
                  <span>Filter</span>
                  <select
                    className="rounded-button bg-warm-alt px-2 py-1 text-caption text-text-primary"
                    onChange={(event) => {
                      const field = (event.target.value ||
                        null) as LookalikeFilterField | null;
                      setFilter(field, null);
                    }}
                    value={filterField ?? ''}
                  >
                    <option value="">All</option>
                    {FILTER_FIELDS.map((field) => (
                      <option key={field} value={field}>
                        {FILTER_FIELD_LABELS[field]}
                      </option>
                    ))}
                  </select>
                  {filterField ? (
                    <select
                      className="rounded-button bg-warm-alt px-2 py-1 text-caption text-text-primary"
                      onChange={(event) => {
                        setFilter(filterField, event.target.value || null);
                      }}
                      value={filterValue ?? ''}
                    >
                      <option value="">Any</option>
                      {filterValueOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.value} ({option.count})
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>

                <div className="ml-auto flex items-center gap-1 rounded-button bg-warm-alt p-1">
                  <SegmentedButton
                    onClick={() => {
                      setDisplayMode('chart');
                    }}
                    selected={displayMode === 'chart'}
                  >
                    Chart
                  </SegmentedButton>
                  <SegmentedButton
                    onClick={() => {
                      setDisplayMode('table');
                    }}
                    selected={displayMode === 'table'}
                  >
                    Table
                  </SegmentedButton>
                  <SegmentedButton
                    onClick={() => {
                      setDisplayMode('poll');
                    }}
                    selected={displayMode === 'poll'}
                  >
                    Poll
                  </SegmentedButton>
                </div>
              </div>

              <div
                className={`min-h-0 flex-1 overflow-y-auto px-4 py-4 ${THIN_SCROLLBAR}`}
              >
                {displayMode === 'chart' ? (
                  <>
                    <LookalikesChart
                      filterField={filterField}
                      filterValue={filterValue}
                      view={view}
                      viewMode={viewMode}
                      xField={xField}
                      yField={yField}
                    />
                    <p className="mt-2 text-caption text-text-muted">
                      {viewMode === 'field'
                        ? 'Positions come directly from the sampled field values.'
                        : 'A real PCA projection over every field at once — points near each other are similar across the whole profile, not just one axis.'}
                    </p>
                    {viewMode === 'similarity' ? (
                      <div className="mt-4">
                        <LookalikesClusters clusters={view.clusters} />
                      </div>
                    ) : null}
                  </>
                ) : displayMode === 'poll' ? (
                  <LookalikesPoll
                    error={pollError}
                    onAsk={runPoll}
                    polling={polling}
                    result={pollResult}
                    view={view}
                  />
                ) : (
                  <LookalikesTable
                    filterField={filterField}
                    filterValue={filterValue}
                    view={view}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
